'use no memo';

import {Alpha3CountryCode, DataStatus, Label, Search, useAnimatedPopper} from 'platform/components';
import {Box, Integer, Scroll, Spinner, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useRef, useState} from 'react';

import {isEmpty} from 'ramda';

import {useGetTenantQuery} from '@dms/api/core';
import {
  CustomerAdvancedSearchResultResponseBody,
  CustomerResponseBodyV2,
  PersonResponseBodyV2,
  useCustomerAdvancedSearchQuery,
  useLazyGetCustomerV2Query,
} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {Nullish, useDebouncedCallback} from 'shared/web';

import {getCustomerName} from '../../utils/getCustomerName';
import {getNaturalPersonFullName} from '../../utils/getNaturalPersonFullName';
import {handleApiError} from '../../utils/handleApiError';
import {CustomerSearchResultItem} from './components/CustomerSearchResultItem';
import {CustomerSelectSearchProps, SanePrimaryContact} from './types';
import {filterCustomerSearchRows} from './utils/filterCustomerSearchRows';
import {getCustomerSearchRowKey, getCustomerSearchRows} from './utils/getCustomerSearchRows';
import {getFormPhoneValue} from './utils/getFormPhoneValue';
import {getSaneCustomerType} from './utils/getSaneCustomerType';

/**
 * Resolves the customer's primary contact into a {@link SanePrimaryContact}.
 *
 * The advanced-search result only carries the primary contact's id, so we match it
 * against the full customer record (founding person + contacts) to recover the
 * structured phone, email and identity-card data. Returns `null` when there is no
 * primary contact or it cannot be matched.
 */
const resolvePrimaryContact = (
  customer: CustomerResponseBodyV2,
  primaryContactId: string | Nullish
): SanePrimaryContact | null => {
  if (!primaryContactId) {
    return null;
  }

  const candidates: (PersonResponseBodyV2 | null)[] = [
    customer.foundingPerson,
    ...customer.contacts,
  ];
  const person = candidates.find((candidate) => candidate?.id === primaryContactId);

  if (!person) {
    return null;
  }

  const phoneNumber = person.phoneNumbers?.[0];

  return {
    id: person.id,
    name: getNaturalPersonFullName(person) ?? '',
    email: person.emails?.[0]?.email || '',
    phoneNumber: getFormPhoneValue(phoneNumber?.number),
    prefix: getFormPhoneValue(phoneNumber?.prefix),
    countryCode: phoneNumber?.countryCode as Alpha3CountryCode,
    identityCards: person.identityCards,
  };
};

const DEFAULT_POPPER_WIDTH_PX = 560;
const SEARCH_MIN_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;
const POPPER_GUTTER_PX = 10;
const RESULTS_MAX_HEIGHT = 100;
const PX_PER_DESIGN_STEP = 4;

export function CustomerSelectSearchAdvanced(props: CustomerSelectSearchProps) {
  const [getCustomer] = useLazyGetCustomerV2Query();
  const {data: _tenant} = useGetTenantQuery();
  const searchRef = useRef<null | HTMLDivElement>(null);

  const [searchString, setSearchString] = useState<string | null>(
    props.defaultSearchString ?? null
  );
  const [debouncedSearchString, setDebouncedSearchString] = useState<string | null>(null);

  const {data: matchingCustomers, isFetching: isFetchingCustomers} = useCustomerAdvancedSearchQuery(
    {name: debouncedSearchString},
    {
      skip: (debouncedSearchString?.length ?? 0) < SEARCH_MIN_LENGTH,
      refetchOnMountOrArgChange: true,
    }
  );

  const {openPopper, referenceRef, popperProps, Popper, closePopper} = useAnimatedPopper({
    placement: 'bottom-start',
    strategy: 'fixed',
    gutter: POPPER_GUTTER_PX,
    // The search input is the popper's reference, not part of the floating content,
    // so a click on it would otherwise be treated as an outside click and close the
    // popper on the same event that focuses/opens it (causing an open-then-close blink).
    shouldIgnoreOutsideClick: (target) => searchRef.current?.contains(target) ?? false,
  });

  const updateDebouncedSearchString = useDebouncedCallback(
    (val: string | null) => setDebouncedSearchString(val),
    SEARCH_DEBOUNCE_MS
  );

  const handleOnChange = (val: string | null) => {
    setSearchString(val);
    updateDebouncedSearchString(val);
    openPopper();
  };

  const handleSelectCustomer = (
    customer: CustomerAdvancedSearchResultResponseBody,
    contactId?: string
  ) => {
    getCustomer({customerId: customer.id})
      .unwrap()
      .then((fetchedCustomer) => {
        const pickedContact = contactId
          ? fetchedCustomer?.contacts?.find((c) => c.id === contactId)
          : undefined;
        const customerInfo =
          pickedContact ?? fetchedCustomer?.foundingPerson ?? fetchedCustomer?.contacts?.[0];

        if (!customerInfo) {
          return;
        }

        const email = customerInfo.emails?.[0]?.email;
        const customerPhoneNumber = customerInfo.phoneNumbers?.[0];
        const name = getCustomerName(fetchedCustomer);

        props.onCustomerSelect({
          type: getSaneCustomerType(customer.discriminator, contactId),
          id: customerInfo.id,
          customerId: fetchedCustomer.id,
          name: name ?? '',
          contactPersonName: getNaturalPersonFullName(customerInfo) ?? '',
          email: email || '',
          phoneNumber: getFormPhoneValue(customerPhoneNumber?.number),
          prefix: getFormPhoneValue(customerPhoneNumber?.prefix),
          countryCode: customerPhoneNumber?.countryCode as Alpha3CountryCode,
          identityCards: customerInfo.identityCards,
          primaryContact: resolvePrimaryContact(fetchedCustomer, customer.primaryContact?.id),
        });

        setSearchString(name ?? '');

        closePopper();
        props.onAfterCustomerSelect?.();
      })
      .catch(handleApiError);
  };

  const popperWidth = Math.floor(
    (props.customPopperWidth ?? searchRef.current?.clientWidth ?? DEFAULT_POPPER_WIDTH_PX) /
      PX_PER_DESIGN_STEP
  ) as Integer;

  const searchRows = filterCustomerSearchRows(getCustomerSearchRows(matchingCustomers), props.type);

  return (
    <>
      <Box ref={referenceRef}>
        <VStack>
          <Label>{i18n.t('entity.document.actions.searchForCustomer')}</Label>
          <Box ref={searchRef}>
            <Search
              value={searchString}
              onChange={handleOnChange}
              onFocus={openPopper}
              onClear={closePopper}
              label={i18n.t('general.labels.searchPlaceholder')}
              isRequired
              isDisabled={props.isDisabled}
            />
          </Box>
        </VStack>
      </Box>
      <Popper {...popperProps}>
        <Box
          width={popperWidth}
          backgroundColor="general.white"
          borderRadius="medium"
          boxShadow="elevation_2"
          overflow="hidden"
        >
          <Box
            paddingHorizontal={4}
            paddingVertical={3}
            borderBottom="1px solid"
            borderColor="general.separator"
          >
            {match(isFetchingCustomers)
              .with(true, () => <Spinner size="small" />)
              .with(false, () => (
                <Text size="small" color="secondary">
                  {i18n.t('entity.customer.labels.resultsFound', {count: searchRows.length})}
                </Text>
              ))
              .exhaustive()}
          </Box>
          <DataStatus
            isLoading={isFetchingCustomers}
            spacing={20}
            isEmpty={isEmpty(searchRows)}
            emptyMessage={i18n.t('entity.customer.labels.noResults')}
          >
            <Scroll auto maxHeight={RESULTS_MAX_HEIGHT}>
              {searchRows.map((row) => (
                <CustomerSearchResultItem
                  key={getCustomerSearchRowKey(row)}
                  row={row}
                  onSelect={handleSelectCustomer}
                  hidePrimaryContact={props.type === 'customer'}
                  data-testid={props['data-testid']}
                />
              ))}
            </Scroll>
          </DataStatus>
        </Box>
      </Popper>
    </>
  );
}
